import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Mascot } from "../brand/Mascot";
import { Tape, HandNote, HandCheck } from "../brand/Doodles";

export const Founder = () => {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative"
      style={{
        paddingTop: "clamp(64px, 10vh, 140px)",
        paddingBottom: "clamp(64px, 10vh, 140px)",
        background:
          "linear-gradient(180deg, transparent 0%, rgba(230,237,247,0.03) 50%, transparent 100%)",
      }}
    >
      {/* legacy anchor compatibility */}
      <span id="about" className="block relative -top-24" aria-hidden />

      <div className="max-w-6xl mx-auto px-5 md:px-8 grid gap-10 md:gap-14 lg:grid-cols-[auto_1fr] items-center">
        {/* Robert (primary) with a small Gadget Guy accent overlapping — clearly secondary */}
        <div className="relative shrink-0 mx-auto lg:mx-0" style={{ width: "clamp(220px, 28vw, 300px)" }}>
          <figure className="ggm-polaroid relative" style={{ transform: "rotate(-2.5deg)" }} data-testid="founder-photo">
            <Tape />
            <div className="overflow-hidden" style={{ aspectRatio: "1 / 1.15", background: "var(--ggm-tech-light)" }}>
              <img
                src="/brand/robert-studio.png"
                alt="Robert — founder of Gadget Guy Media"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            <figcaption className="ggm-hand pt-3 text-center text-lg" style={{ color: "var(--ggm-navy)" }}>
              Robert · Gallatin, TN
            </figcaption>
          </figure>
          {/* Small overlapping Gadget Guy accent — transparent artwork, no frame/mask needed */}
          <div
            className="hidden md:block absolute"
            style={{
              right: -32,
              bottom: -24,
              width: 110,
            }}
            data-testid="founder-mascot-frame"
            aria-hidden
          >
            <Mascot pose="welcoming" size="sm" className="mx-auto" style={{ height: 150, width: "auto" }} />
          </div>
        </div>

        <div>
          <HandNote testId="founder-eyebrow" className="text-lg mb-4">The human behind the robot</HandNote>
          <h2
            className="font-condensed font-bold text-white"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
            data-testid="founder-headline"
          >
            Hi, I'm Robert.
          </h2>

          <div className="mt-6 space-y-5 max-w-2xl font-body text-white/80 text-base md:text-lg leading-relaxed">
            <p>I've spent more than 30 years working with technology and marketing.</p>
            <p>
              Today I use AI to do something that would have required an entire
              development team not long ago:
            </p>
            <p
              className="font-condensed font-bold uppercase text-[var(--ggm-signal-cyan)]"
              style={{
                fontSize: "clamp(1.15rem, 2.5vw, 1.75rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
              }}
            >
              Turn ideas into working software.
            </p>
            <p>I build my own products.</p>
            <p>I build AI-powered systems for businesses.</p>
            <p>And I teach people how to build their own.</p>
            <p>
              I'm not interested in AI because it's trendy. I'm interested in what
              happens when you use it to solve an actual problem.
            </p>
          </div>

          <ul
            className="mt-8 grid gap-2 max-w-md font-condensed uppercase tracking-widest text-[13px] text-white/85"
            data-testid="founder-emphasis"
          >
            <li className="flex items-center gap-3">
              <span className="text-white/60"><HandCheck color="var(--ggm-signal-cyan)" size={22} /></span>
              No giant agency.
            </li>
            <li className="flex items-center gap-3">
              <span className="text-white/60"><HandCheck color="var(--ggm-signal-cyan)" size={22} /></span>
              No six-month development cycle.
            </li>
            <li className="flex items-center gap-3">
              <span className="text-white/60"><HandCheck color="var(--ggm-signal-cyan)" size={22} /></span>
              Just an idea and the ability to ship it.
            </li>
          </ul>

          <div className="mt-8">
            <Link
              to="/robert"
              className="ggm-btn-secondary"
              data-testid="founder-cta"
            >
              More about me <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founder;
